// LeadCreateDialog — диалог ручного создания лида (source=MANUAL; обязателен только заголовок).

import { useState, type ChangeEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { leadsApi } from '@/api/leads';
import { extractApiErrorMessage } from '@/lib/apiError';
import { t } from '@/i18n';

/** Значения формы создания лида ('' = null на проводе). */
interface LeadCreateFormValues {
  title: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  companyName: string;
  licenseNumber: string;
  siteName: string;
  ipAddress: string;
  notes: string;
}

const EMPTY_CREATE_FORM: LeadCreateFormValues = {
  title: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  companyName: '',
  licenseNumber: '',
  siteName: '',
  ipAddress: '',
  notes: '',
};

interface LeadCreateDialogProps {
  /** Открыт ли диалог. */
  open: boolean;
  /** Закрыть без сохранения; также вызывается после успешного POST. */
  onClose: () => void;
}

/**
 * Диалог ручного создания лида.
 * Контракт: открыт при `open`; submit POST source=MANUAL; успех — invalidate `['leads']` и `onClose`.
 */
export function LeadCreateDialog({ open, onClose }: LeadCreateDialogProps): JSX.Element {
  const queryClient = useQueryClient();
  const [values, setValues] = useState<LeadCreateFormValues>(EMPTY_CREATE_FORM);

  const createMutation = useMutation({
    mutationFn: (v: LeadCreateFormValues) =>
      leadsApi.create({
        title: v.title,
        firstName: v.firstName || null,
        lastName: v.lastName || null,
        email: v.email || null,
        phone: v.phone || null,
        companyName: v.companyName || null,
        licenseNumber: v.licenseNumber || null,
        siteName: v.siteName || null,
        ipAddress: v.ipAddress || null,
        notes: v.notes || null,
        source: 'MANUAL',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['leads'] });
      setValues(EMPTY_CREATE_FORM);
      onClose();
    },
  });

  const setField = (field: keyof LeadCreateFormValues) => (e: ChangeEvent<HTMLInputElement>) =>
    setValues((v) => ({ ...v, [field]: e.target.value }));

  const cannotSubmit = createMutation.isPending || !values.title.trim();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('leads.create.dialogTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {createMutation.isError ? (
            <Alert severity="error">{extractApiErrorMessage(createMutation.error, t('leads.create.error'))}</Alert>
          ) : null}
          <TextField
            label="Заголовок"
            required
            fullWidth
            value={values.title}
            onChange={setField('title')}
            inputProps={{ 'aria-label': 'Заголовок' }}
          />
          <TextField
            label="Имя"
            fullWidth
            value={values.firstName}
            onChange={setField('firstName')}
            inputProps={{ 'aria-label': 'Имя' }}
          />
          <TextField
            label="Фамилия"
            fullWidth
            value={values.lastName}
            onChange={setField('lastName')}
            inputProps={{ 'aria-label': 'Фамилия' }}
          />
          <TextField
            label="Email"
            fullWidth
            value={values.email}
            onChange={setField('email')}
            inputProps={{ 'aria-label': 'Email' }}
          />
          <TextField
            label="Телефон"
            fullWidth
            value={values.phone}
            onChange={setField('phone')}
            inputProps={{ 'aria-label': 'Телефон' }}
          />
          <TextField
            label="Компания"
            fullWidth
            value={values.companyName}
            onChange={setField('companyName')}
            inputProps={{ 'aria-label': 'Компания' }}
          />
          <TextField
            label="Номер лицензии"
            fullWidth
            value={values.licenseNumber}
            onChange={setField('licenseNumber')}
            inputProps={{ 'aria-label': 'Номер лицензии' }}
          />
          <TextField
            label="Имя сайта"
            fullWidth
            value={values.siteName}
            onChange={setField('siteName')}
            inputProps={{ 'aria-label': 'Имя сайта' }}
          />
          <TextField
            label="IP адрес"
            fullWidth
            value={values.ipAddress}
            onChange={setField('ipAddress')}
            inputProps={{ 'aria-label': 'IP адрес' }}
          />
          <TextField
            label="Заметки"
            fullWidth
            multiline
            minRows={3}
            value={values.notes}
            onChange={setField('notes')}
            inputProps={{ 'aria-label': 'Заметки' }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          onClick={() => createMutation.mutate(values)}
          disabled={cannotSubmit}
        >
          {t('leads.create.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
